import { useState } from 'react'
import { dateFromString, dateString, type TimeEntry } from '@coneno/c-sheet-shared'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { useCreateTimeEntry } from '@/hooks/use-timesheets'

type CloneEntryDialogProps = {
  /** The entry to duplicate; the dialog is open while one is set. */
  entry: TimeEntry | null
  onClose(): void
}

/** Asks for the date an entry should be duplicated to and creates the copy. */
export function CloneEntryDialog({ entry, onClose }: CloneEntryDialogProps): React.JSX.Element {
  const clone = useCreateTimeEntry()
  // The chosen date belongs to one entry; a different entry starts from its own date again.
  const [chosen, setChosen] = useState<{ entryId: string; date: string } | null>(null)
  const cloneDate = (chosen?.entryId === entry?.id ? chosen?.date : null) ?? entry?.workDate ?? ''

  function close(): void {
    clone.reset()
    onClose()
  }

  function cloneEntry(): void {
    if (!entry) return
    clone.mutate(
      {
        personId: entry.personId,
        projectId: entry.projectId,
        taskId: entry.taskId,
        workDate: cloneDate,
        startTime: entry.startTime,
        endTime: entry.endTime,
        description: entry.description,
        billable: entry.billable
      },
      { onSuccess: close }
    )
  }

  return (
    <Dialog
      open={entry !== null}
      onOpenChange={(open) => {
        if (!open && !clone.isPending) close()
      }}
    >
      <DialogContent className="sm:max-w-xl" showCloseButton={!clone.isPending}>
        <DialogHeader>
          <DialogTitle>Clone entry to…</DialogTitle>
          <DialogDescription>
            Select the date where this entry should be duplicated.
          </DialogDescription>
        </DialogHeader>
        {entry && (
          <Calendar
            key={entry.id}
            mode="single"
            required
            numberOfMonths={2}
            selected={dateFromString(cloneDate)}
            onSelect={(date) => {
              if (date) setChosen({ entryId: entry.id, date: dateString(date) })
            }}
            defaultMonth={dateFromString(cloneDate)}
            className="mx-auto rounded-lg border"
          />
        )}
        {clone.error && (
          <p role="alert" className="text-sm text-destructive">
            {clone.error.message}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" disabled={clone.isPending} onClick={close}>
            Cancel
          </Button>
          <Button type="button" disabled={!entry || clone.isPending} onClick={cloneEntry}>
            {clone.isPending ? 'Cloning…' : 'Clone entry'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
